import { createContext, useContext } from 'react';
import type { PresetName, Ruleset } from '../../core';
import type { CustomPresetEntry } from './customPresets';
import type { ImportSummary, ResolvedImport } from './customPresetIO';

/**
 * Reactive access to the saved custom QC presets (cue-forge-3r9). The pure store
 * in `customPresets.ts` owns `localStorage`; this context is the live snapshot the
 * QC preset pickers, `QcProvider`, `Editor`, and `BatchPanel` all read so a save in
 * the editor re-runs QC and re-labels every `<select>` without a manual refresh.
 * Kept component-free so the provider file satisfies react-refresh's
 * components-only rule.
 */
export interface CustomPresetsValue {
  /** The current saved presets — a fresh array reference on every mutation. */
  presets: readonly CustomPresetEntry[];
  /** Monotonic; bumps on every save/rename/remove/reload and cross-tab storage event. */
  revision: number;
  /**
   * Create a new preset, or update one in place when `id` is given. Returns the
   * saved entry. Propagates {@link CustomPresetStorageError} to the caller (the
   * editor surfaces quota / soft-cap / non-secure-context failures). Saving by an
   * `id` that was removed elsewhere falls back to a fresh create ("save as new").
   */
  save: (ruleset: Ruleset, opts?: { id?: string; basedOn?: PresetName }) => CustomPresetEntry;
  /** Rename a preset by id; `null` means it was removed elsewhere (P4 prompts). */
  rename: (id: string, name: string) => CustomPresetEntry | null;
  /** Delete a preset by id; returns whether anything was removed. */
  remove: (id: string) => boolean;
  /**
   * Apply resolved imports (cue-forge-4o9) in one atomic write and return the
   * added/updated/skipped summary. Every add mints a fresh id; only an explicit
   * `overwrite` touches an existing entry. Throws {@link CustomPresetStorageError}
   * on a non-secure context (needed to mint ids) or a storage failure.
   */
  importPresets: (items: ResolvedImport[]) => ImportSummary;
  /** The live entry for an id, or `undefined`. */
  get: (id: string) => CustomPresetEntry | undefined;
  /** Re-read the store into state (self-heals a corrupt blob) and bump revision. */
  reload: () => void;
}

/** Stable empty snapshot so consumers outside a provider keep a constant identity. */
const EMPTY: readonly CustomPresetEntry[] = [];

/**
 * Fallback returned outside a provider — some isolated component tests mount
 * `QcProvider`/`QcThresholds`/`Editor` without one. There are no stored presets to
 * resolve then, and authoring surfaces (which only mount inside the provider)
 * never call the mutators, so `save` throws to catch genuine misuse.
 */
const FALLBACK: CustomPresetsValue = {
  presets: EMPTY,
  revision: 0,
  save() {
    throw new Error('save must be used within a CustomPresetsProvider');
  },
  rename: () => null,
  remove: () => false,
  importPresets() {
    throw new Error('importPresets must be used within a CustomPresetsProvider');
  },
  get: () => undefined,
  reload: () => {},
};

export const CustomPresetsContext = createContext<CustomPresetsValue | null>(null);

/** Access the live custom-preset snapshot. Returns an empty fallback outside a provider. */
export function useCustomPresets(): CustomPresetsValue {
  return useContext(CustomPresetsContext) ?? FALLBACK;
}
